import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, Dropdown, Menu, Checkbox } from "antd";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import useGetPrices from "../../hooks/getPrices/useGetPrices";
import { TimeFormatter } from "helpers/TimeFormatter/TimeFormatter";
import { DownOutlined } from "@ant-design/icons";
import "./PricesPage.css";
import { PriceFormatter } from "helpers/PriceFormatter/PriceFormatter";
import useUpdatePrices from "../../hooks/updatePrices/useUpdatePrices";
import ExportTableButton from "layouts/LayoutTableExport/LayoutTableExport";
import TableSkeleton from "../../ui/table/TableSkeleton";
import { TABLE_PAGINATION_UX } from "../../ui/table/paginationDefaults";
import CopyableTruncatedCell from "../../components/CopyableTruncatedCell";

interface Prices {
  itemId: string;
  refId: string;
  updateDate: string;
  productName: string;
  priceVtex: number;
  priceSiesa: number;
  listPriceVtex: string;
  listPriceSiesa: string;
}

const PricesPage: React.FC = () => {
  const { prices, loading: fetching, fetchPrices, totalElements } =
    useGetPrices();
  const { fetchUpdatePrices, renderModal } = useUpdatePrices();

  const [loading, setLoading] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState<React.Key[]>([
    "itemId",
    "refId",
    "updateDate",
    "productName",
    "priceVtex",
    "listPriceVtex",
    "priceSiesa",
    "listPriceSiesa",
  ]);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
      current: 1,
      pageSize: 10,
      total: 0,
    });
  

  const allColumns: TableColumnsType<Prices> = useMemo(
    () => [
      {
        title: "Item ID",
        dataIndex: "itemId",
        key: "itemId",
        width: 110,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value}
            copySuccessMessage="Item ID copiado"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "ID Ref.",
        dataIndex: "refId",
        key: "refId",
        width: 110,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value}
            copySuccessMessage="ID Ref. copiado"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "Fecha Actualización",
        dataIndex: "updateDate",
        key: "updateDate",
        width: 140,
      },
      {
        title: "Producto",
        dataIndex: "productName",
        key: "productName",
        width: 160,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value || "—"}
            copySuccessMessage="Producto copiado"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "Lista Vtex",
        dataIndex: "listPriceVtex",
        key: "listPriceVtex",
        width: 110,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value || "—"}
            copySuccessMessage="Lista VTEX copiada"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "Precio Vtex",
        dataIndex: "priceVtex",
        key: "priceVtex",
        width: 110,
        render: (total: number) => {
          const formatted = PriceFormatter(total);
          return (
            <CopyableTruncatedCell
              value={formatted}
              copySuccessMessage="Precio VTEX copiado"
              onlyWhenTruncated={false}
            />
          );
        },
      },
      {
        title: "Lista Siesa",
        dataIndex: "listPriceSiesa",
        key: "listPriceSiesa",
        width: 110,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value || "—"}
            copySuccessMessage="Lista Siesa copiada"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "Precio Siesa",
        dataIndex: "priceSiesa",
        key: "priceSiesa",
        width: 110,
        render: (total: number) => {
          const formatted = PriceFormatter(total);
          return (
            <CopyableTruncatedCell
              value={formatted}
              copySuccessMessage="Precio Siesa copiado"
              onlyWhenTruncated={false}
            />
          );
        },
      },
    ],
    []
  );

  const filteredColumns = allColumns.filter((column) =>
    selectedColumns.includes(column.key!)
  );

  const handleColumnSelection = (columnKey: React.Key) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey) 
        : [...prev, columnKey] 
    );
  };

  const onReload = () => {
    setLoading(true);
    fetchUpdatePrices()
    fetchPrices(pagination.current || 1, pagination.pageSize || 10).finally(() => setLoading(false));
  };

  const formattedPrices = prices?.map((item) => ({
    ...item,
    updateDate: TimeFormatter(item?.updateDate),
    uniqueKey: `${item.itemId}-${item.refId}`,
  }));

  useEffect(() => {
    fetchPrices(pagination.current || 1, pagination.pageSize || 10);
  }, [fetchPrices, pagination]);

  const handleTableChange = (next: TablePaginationConfig) => {
    setPagination(next);
    fetchPrices(next.current || 1, next.pageSize || 10);
  };

  const menu = (
    <Menu>
      {allColumns.map((column) => (
        <Menu.Item key={String(column.key)}>
          <Checkbox
            checked={selectedColumns.includes(column.key!)}
            onChange={() => handleColumnSelection(column.key!)}
          >
            {typeof column.title === "string" ? column.title : ""}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  const isBusy = loading || fetching;
  
  return (
    <>
      <div className="page__content">
        <div className="module-toolbar">
          <div className="module-toolbar__side module-toolbar__side--start">
            <div className="module-toolbar__group module-toolbar__group--actions">
              <span className="module-toolbar__label">Acciones</span>
              <div className="module-toolbar__control-row">
                <Button
                  type="primary"
                  onClick={onReload}
                  loading={isBusy}
                >
                  {isBusy ? "Actualizando" : "Actualizar"}
                </Button>
                {renderModal()}
                <ExportTableButton
                  data={formattedPrices || []}
                  columns={filteredColumns}
                  disabled={
                    isBusy || !formattedPrices || formattedPrices?.length === 0
                  }
                  variant="default"
                />
              </div>
            </div>
            <div className="module-toolbar__separator" aria-hidden="true" />
          </div>

          <div className="module-toolbar__middle" />

          <div className="module-toolbar__side module-toolbar__side--end">
            <div className="module-toolbar__group module-toolbar__group--columns">
              <span className="module-toolbar__label">Columnas a mostrar</span>
              <Dropdown overlay={menu} trigger={["click"]} disabled={isBusy}>
                <Button
                  className="module-toolbar__columns-btn"
                  disabled={isBusy}
                >
                  Seleccionar Columnas <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
        {fetching && (!prices || prices.length === 0) ? (
          <TableSkeleton />
        ) : (
          <div className="data-enter">
            <Table<Prices>
              columns={filteredColumns}
              dataSource={formattedPrices || []}
              loading={fetching}
              rowKey="uniqueKey"
              pagination={{
                ...pagination,
                ...TABLE_PAGINATION_UX,
                total: totalElements,
                showTotal: (total) =>
                  `${total.toLocaleString("es-CO")} ${total === 1 ? "ítem" : "ítems"}`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 980 }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default PricesPage;
